import { useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from 'react-query';
import { getDesignMetaDataByUUID, getLatLng, verifyPublicKey, verifyPanelKey, getDesignMetdaDataByExternalId, getThemeData } from './api';
import ROUTES from 'constants/routes';
import Loader, { Loader_Type } from 'components/Loader';
import { useEffect, useState } from 'react';
import {
	API_ERROR_CODES,
	DEFAULT_ELECTRIC_UNIT_PRICE,
	DEFAULT_SHADING_VAL,
	ENERGY_DERATE_RATE,
	SOlAR_QOUTE_APP_NAME,
	TOOL_RENDER_MODE,
} from '../../constants';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store';
import { setAuth } from 'store/slices/AuthSlice/authSlice';
import { fetchRoofData, setOrgData } from 'store/slices/RoofDataSlice/roofDataSlice';
import { setQueryParamState } from 'store/slices/QueryParamsSlice';
import { QueryParamsFromParentApp } from 'store/slices/QueryParamsSlice/types';
import { validateLatLng } from './util';
import { decideDerateBasedUponMode } from '../../util';
import { useThemeDispatch } from 'contexts/ThemeContext';
import useNavigateWithQueryParams from 'hooks/useNavigateWithQueryParams';
import { TOOL_ERROR_CODES } from 'constants/toolError';
import { postToolError } from 'parentEvents';
import { getDisplayModeFromUrl } from 'store/slices/QueryParamsSlice/util/getInitialState';

export default function Home() {
	const [defaultPanelKey, setDefaultPanelKey] = useState<string>();
	const [error, setError] = useState('');
	// get query param from url
	// verify  it set auth
	// and lead to next page
	// ?publicKey='ee'?'externalKey'
	const dispatch = useDispatch<AppDispatch>();
	const themeDispatch= useThemeDispatch();
	const { navigateWithQueryParams } = useNavigateWithQueryParams();

	const [queryParams] = useSearchParams();

	const publicKey = queryParams.get('public-key');
	const externalId = queryParams.get('external-id');
	const billAmount = queryParams.get('bill-amount');
	const aiToolPanelKeys = queryParams.get('ai-tool-panel-keys');
	const address = queryParams.get('address') as string;
	const lat = queryParams.get('lat');
	const lng = queryParams.get('lng');
	const annualUsage = queryParams.get('annual-usage');
	const electricUnitPrice = queryParams.get('electric-unit-price');
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const panelKey = queryParams.get('panel-key')?.trim() ? decodeURIComponent(queryParams.get('panel-key')!) : undefined;
	const parentAppName = queryParams.get('parent-id') || '';
	const designUUID = queryParams.get('design-uuid');
	const showNextSteps = queryParams.get('show-steps');
	const energyDerateRate = queryParams.get('energy-derate-rate');
	const canRemoveShading = queryParams.get('can-remove-shading');
	const showLidarDate = queryParams.get('show_lidar_date');
	//  setbacks
	const gutterSetback = queryParams.get('gutter_setback');
	const nonGutterSetback = queryParams.get('other_setback');
	const obstructionSetback = queryParams.get('obstruction_setback');
	const eavesSetback = queryParams.get('eaves_setback');
	const rakesSetback = queryParams.get('rakes_setback');
	const ridgesSetback = queryParams.get('ridges_setback');
	const valleysSetback = queryParams.get('valleys_setback');
	const hipsSetback = queryParams.get('hips_setback');

	const stateCode = queryParams.get('state-code');
	const canEditSetback = queryParams.get('can-edit-setback');
	const addNewPanel = queryParams.get('add-new-panel');
	const minPanel = queryParams.get('min-panel');
	const minPanelValue = minPanel !== null && minPanel.trim() !== '' && !Number.isNaN(+minPanel) ?
		+minPanel : undefined;
	
	const toolType = queryParams.get('tool-type')?.toLowerCase() ?? 'solar';
	const displayMode = getDisplayModeFromUrl();
	const defaultShading = queryParams.get('default-shading') ?? DEFAULT_SHADING_VAL;
	const roleHash = queryParams.get('role');
	console.log('rolehash: ', roleHash);

	const toolRenderMode = (queryParams.get('tool-mode')?.toLowerCase() ?? TOOL_RENDER_MODE.DEFAULT) as TOOL_RENDER_MODE;

	const validationRules: Array<{ condition: boolean; message: string }> = [
		// required params
		{ condition: !roleHash?.length, message: 'role is required' },
		{ condition: !externalId && !designUUID, message: 'external id or designuuid is required' },
		{ condition: !publicKey, message: 'public key is required' },

		// address / coordinates
		{ condition: !address && (!lat || !lng), message: 'address or lat,lng is required' },
		{ condition: !address, message: 'address is required' },
		{ condition: !!(lat && lng && !validateLatLng(lat, lng)), message: 'lat, lng is not valid' },

		// optional value validations
		{
			condition: toolType.includes('solar') && (!billAmount || Number.isNaN(+billAmount) || +billAmount < 50 || +billAmount > 20000),
			message: 'enter valid bill amount',
		},
		{
			condition: electricUnitPrice !== null && (isNaN(+electricUnitPrice) || !(+electricUnitPrice)),
			message: 'Invalid electric unit price',
		},
		{
			condition: annualUsage !== null && (isNaN(+annualUsage) || !(+annualUsage)),
			message: 'Invalid annual usage',
		},
	];

	const validationError = validationRules.find(rule => rule.condition);
	if (validationError) {
		postToolError(TOOL_ERROR_CODES.INVALID_QUERY_PARAMS, new Error(validationError.message));
		return <p>{validationError.message}</p>;
	}

	localStorage.setItem(`roleHash-${externalId}`, roleHash as string);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const publicKeyVerificationQuery = useQuery('verify', () => verifyPublicKey(publicKey as string), {
		onSuccess: (data: boolean) => {
			try {
				if (!data) {
					const err: any = new Error('Invalid Public Key');
					err.code = TOOL_ERROR_CODES.PUBLIC_KEY_INVALID;
					throw err;
				}
				sessionStorage.setItem('SOLAR_QUOTE_EMBED_Authorization', publicKey as string);
				dispatch(setAuth({ token: publicKey as string }));
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (error?.code === TOOL_ERROR_CODES.PUBLIC_KEY_INVALID) {
					postToolError(TOOL_ERROR_CODES.PUBLIC_KEY_INVALID, error);
				} else {
					setError('Please grant all required permissions and refresh the page to run the application correctly');
					postToolError(TOOL_ERROR_CODES.PERMISSION_DENIED, error);
				}
				console.log('errr', error);
			}
		},
		onError: (err) => {
			console.log('err', err);
			postToolError(TOOL_ERROR_CODES.PUBLIC_KEY_INVALID, err);
		},
		refetchOnWindowFocus: false,
		cacheTime: 0,
		retry: 2
	});

	const onThemeDataSuccess = (data: organization) => {
		try {
			if (!data) throw new Error('No Organization found.');
			themeDispatch({type: 'SET_THEME', payload: {settings: data.settings, parentAppName, config: data.configurations}});
			dispatch(setOrgData(data.settings));
			setDefaultPanelKey(defaultPanelKey);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			// setError('Please grant all required permissions and refresh the page to run the application correctly');
			postToolError(TOOL_ERROR_CODES.THEME_LOAD_FAILED, error);
			console.log('errr', error);
		}
	};

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const themeData = useQuery('themeData', () => getThemeData(publicKey as string), {
		onSuccess: onThemeDataSuccess,
		onError: (err) => postToolError(TOOL_ERROR_CODES.THEME_LOAD_FAILED, err),
		refetchOnWindowFocus: false,
		cacheTime: 0,
		retry: 2,
		enabled: !!publicKeyVerificationQuery.data
	});
	// console.log(themeData);

	const shouldCallPanelKeyQuery = toolType !== 'roof' && !!panelKey && publicKeyVerificationQuery.data && Boolean(themeData?.data);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const panelKeyExistsQuery = useQuery('panelKey', () => verifyPanelKey(panelKey as string, publicKey as string), {
		enabled: shouldCallPanelKeyQuery,
		refetchOnWindowFocus: false,
		cacheTime: 0,
		onSuccess: (data: boolean) => {
			console.log('panel key exists', data);
			if (!data) {
				postToolError(TOOL_ERROR_CODES.PANEL_KEY_INVALID, new Error(`panel key ${panelKey} does not exist, please contact your organization admin.`));
			}
		},
		onError: (err) => postToolError(TOOL_ERROR_CODES.PANEL_KEY_INVALID, err),
	});

	const shouldCallAddressAndExternalIdQueries = (toolType !== 'roof' && panelKey) ? !!panelKeyExistsQuery.data : !!publicKeyVerificationQuery.data;
	const shouldCallAddressQuery = !(lat && lng);
	const queries = designUUID ? [
		{
			queryKey: ['designUUID', designUUID],
			queryFn: () => getDesignMetaDataByUUID(designUUID as string, publicKey as string),
			enabled: shouldCallAddressAndExternalIdQueries,
		}] : [
		{
			queryKey: ['externalIdData', externalId],
			queryFn: () => getDesignMetdaDataByExternalId(externalId || '', publicKey as string),
			enabled: shouldCallAddressAndExternalIdQueries,
		}];
	if(shouldCallAddressQuery)
		queries.push({
			queryKey: ['getAddress', address],
			queryFn: () => getLatLng(address, publicKey as string),
			enabled: shouldCallAddressAndExternalIdQueries,
			onError: (err: unknown) => {
				postToolError(TOOL_ERROR_CODES.ADDRESS_RESOLVE_FAILED, err);
				setError('Failed to resolve address. Please try again.');
			},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} as any);
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const addressAndDataQuery = useQueries(queries);

	const allFinished = addressAndDataQuery.every(query => query.isSuccess);
	// eslint-disable-next-line react-hooks/rules-of-hooks
	useEffect(() => {
		if (allFinished && !error) {
			const [designData, addressData] = addressAndDataQuery;
			if (designData?.data.statusCode == 401) {
				postToolError(TOOL_ERROR_CODES.DESIGN_LOAD_FAILED, new Error('You are not authorized to view this design.'));
				setError('You are not authorized to view this design.');
				return;
			}
			if (designData?.data.statusCode == 500){
				postToolError(TOOL_ERROR_CODES.DESIGN_LOAD_FAILED, new Error('Internal server error'));
				setError('Internal server error');
				return;
			}
			if (designUUID && (!designData.data || designData?.data?.errorCode === API_ERROR_CODES.DESIGN_DELETED)) {
				const msg = designData.data?.errorCode === API_ERROR_CODES.DESIGN_DELETED ?
					`uuid ${designUUID} has been deleted` : `uuid ${designUUID} has no data assoicated with it`;
				postToolError(TOOL_ERROR_CODES.DESIGN_LOAD_FAILED, new Error(msg));
				setError(msg);
				return;
			}
			if(externalId && (designData.data.errorCode === API_ERROR_CODES.DESIGN_DELETED)) {
				setError(`external id ${externalId} has been deleted`);
				return;
			}
			// for handle genric error from design api
			if (designData?.data?.error) {
				const msg = designData.data.message || designData.data.error;
				postToolError(TOOL_ERROR_CODES.DESIGN_LOAD_FAILED, new Error(msg));
				setError(msg);
				return;
			}
			const coordinates = shouldCallAddressQuery ? addressData.data : {lat: +lat,lng: +lng};

			const queryParmaObj: QueryParamsFromParentApp = {
				billAmount: billAmount!=null ? +billAmount: null,
				designUUID: designData.data.designUUID,
				coordinates: coordinates,
				electricUnitPrice: +(Number(electricUnitPrice).toFixed(2) || DEFAULT_ELECTRIC_UNIT_PRICE),
				externalId: designData.data.externalId,
				panelKey,
				parentAppName,
				lastEnergyInputChange: annualUsage ? 'annualEnergy' : designData.data.lastEnergyInputChange,
				annualEnergy: +(annualUsage || designData.data.annualEnergy),
				energyDerateRate: energyDerateRate ? +energyDerateRate : ENERGY_DERATE_RATE,
				derateRateBeforeUserSwitchToNonDefaultModes: energyDerateRate ? +energyDerateRate : ENERGY_DERATE_RATE,
				showNextSteps: showNextSteps === 'true',
				canRemoveShading: canRemoveShading === 'true',
				gutterSetback: gutterSetback ? (+gutterSetback) : undefined,
				nonGutterSetback: nonGutterSetback ? (+nonGutterSetback) : undefined,
				obstructionSetback: obstructionSetback ? (+obstructionSetback): undefined,
				canEditSetback: canEditSetback === 'true',
				aiToolPanelKeys: aiToolPanelKeys?.length? aiToolPanelKeys.split(',') : undefined,
				stateCode: stateCode ?? undefined,
				toolType: toolType as ToolType,
				displayMode,
				energyDerateRateFromParent: energyDerateRate ? +energyDerateRate : undefined,
				defaultShading: +defaultShading,
				showLidarDate: showLidarDate === 'true',
				eavesSetback:eavesSetback ? (+eavesSetback) : undefined,
				rakesSetback:rakesSetback ? (+rakesSetback) : undefined,
				ridgesSetback:ridgesSetback ? (+ridgesSetback) : undefined,
				valleysSetback:valleysSetback ? (+valleysSetback) : undefined,
				hipsSetback:hipsSetback ? (+hipsSetback) : undefined,
				toolRenderMode,
				minPanel: minPanelValue,
				addNewPanel: addNewPanel === 'true',
			};
			let navigationRoute;
			if (designData.data) {
				let modeEnergyRate = energyDerateRate ? +energyDerateRate : ENERGY_DERATE_RATE;

				modeEnergyRate = decideDerateBasedUponMode(designData.data.mode, queryParmaObj.energyDerateRate, stateCode);
				queryParmaObj.derateRateBeforeUserSwitchToNonDefaultModes = queryParmaObj.energyDerateRate;
				queryParmaObj.energyDerateRate = modeEnergyRate;

				dispatch(setQueryParamState(queryParmaObj));
				const panelKeyForDesign = panelKey || defaultPanelKey;
				dispatch(fetchRoofData(
					{
						uuid: designData.data.designUUID as string,
						panelKey: panelKeyForDesign, queryParamBillAmount: billAmount!=null ? +billAmount: undefined,
						designType: designData.data.designType
					}));
				window.parent.postMessage({ id: 'SOLARQUOTEEMBED_SHOW_DATA' }, '*');
				navigationRoute = ROUTES.SHOW_DATA;
			} else {
				queryParmaObj.externalId = externalId as string;
				dispatch(setQueryParamState(queryParmaObj));
				navigationRoute = ROUTES.SHOW_MAP;
			}
			navigateWithQueryParams(navigationRoute, {
				state: {
					coordinates: queryParmaObj.coordinates
				}
			});

		}
	}, [allFinished, navigateWithQueryParams]);

	if (error) {
		return <div style={{width: '100%', textAlign: 'center', position: 'relative', top: '20vh'}}>{error}</div>;
	}

	if (publicKeyVerificationQuery.isLoading || panelKeyExistsQuery.isLoading) {
		const type = (parentAppName === SOlAR_QOUTE_APP_NAME && toolType === 'solar') ? Loader_Type.SLIDER : Loader_Type.DEFAULT;
		return <Loader type={type}/>;
	}

	const runtimeValidationRules: Array<{ condition: boolean; message: string }> = [
		{ condition: !publicKeyVerificationQuery.data, message: 'Unauthorized' },
		{ condition: toolType !== 'roof' && !!panelKey && !panelKeyExistsQuery.data, message: `panel key ${panelKey} does not exist, please contact your organization admin.` },
	];

	const runtimeError = runtimeValidationRules.find(rule => rule.condition);
	if (runtimeError) {
		return <div style={{width: '100%', textAlign: 'center', position: 'relative', top: '20vh'}}>{runtimeError.message}</div>;
	}

	return null;
}
